import { CardsSpeiOrigin } from './cards-spei-order.model';
import { CardsSpeiOrderKeys } from './cards-spei-order-attempt';

/**
 * Tipos de la memoria de intentos por nodo de las órdenes SPEI de Cards
 * (feature 124). La memoria vive en la PESTAÑA (singleton raíz, solo heap) y se
 * borra al cerrar sesión; los use-cases siguen en la página.
 */

/** Nodos recordados por tipo (individual y lote por separado); se desaloja el usado hace más tiempo. */
export const CARDS_SPEI_ATTEMPTS_CAPACITY = 32;

/**
 * Llaves del intento de la individual + la referencia numérica del cuerpo que lo
 * abrió: entra en la huella, así que proponerla al volver al nodo es lo que deja
 * que el mismo destino, monto y concepto formen la MISMA huella (R10).
 */
export type CardsSpeiIndividualAttemptKeys = CardsSpeiOrderKeys & { readonly reference: string };

/** Llaves de un lote: la del lote y, por fila enviada, su clave de rastreo y referencia. */
export interface CardsSpeiBatchKeys {
  /** `idempotency_key` del lote (PR #281 de banca). */
  readonly idempotencyKey: string;
  readonly trackingKeys: readonly string[];
  readonly references: readonly string[];
}

/**
 * Identidad del nodo de origen: nivel + id público del SERVIDOR (el store ya
 * falla cerrado si la respuesta es de otro nodo). Dos niveles nunca comparten clave.
 */
export function cardsSpeiNodeOf(origin: Pick<CardsSpeiOrigin, 'level' | 'publicId'>): string {
  return `${origin.level}#${origin.publicId}`;
}

/**
 * Lotes "en duda" (feature 122, R16): lotes cuyo último envío desde el nodo terminó
 * con desenlace DESCONOCIDO (red, `timeout`, `5xx`, "sin confirmar", "ya se está
 * procesando", `IDEMPOTENCY_KEY_UNRESOLVED`). Mientras un lote está en duda, la
 * revisión no ofrece eliminar filas: quitar una válida cambia la huella ⇒ llave
 * nueva, y si el envío anterior SÍ se registró, el reenvío "corregido" sería un
 * SEGUNDO lote para las filas que quedan.
 *
 * - La marca es por nodo Y huella EXACTA (`cardsSpeiBatchFingerprint`): otro archivo
 *   (otra huella) no hereda la duda; volver a subir el MISMO archivo sí.
 * - Un nodo puede tener varios lotes en duda (A quedó en duda y luego se envió B):
 *   confirmar B no confirma A, así que `forget` olvida SOLO la huella indicada.
 * - Tope como `SpeiAttemptsByNode` (LRU por nodo y por huella dentro del nodo).
 * - Solo heap, como las llaves: nada se persiste ni se registra; se vacía al cerrar
 *   sesión (`CardsSpeiAttemptMemory.clear()`).
 */
export class CardsSpeiBatchDoubts {
  private readonly doubts = new Map<string, Set<string>>();

  constructor(private readonly capacity: number) {}

  /** El lote `fingerprint` de `node` quedó con desenlace desconocido. */
  mark(node: string, fingerprint: string): void {
    const set = this.doubts.get(node) ?? new Set<string>();
    this.doubts.delete(node);
    set.delete(fingerprint);
    set.add(fingerprint);
    while (set.size > this.capacity) {
      const oldest = set.values().next().value;
      if (oldest === undefined) break;
      set.delete(oldest);
    }
    this.doubts.set(node, set);
    while (this.doubts.size > this.capacity) {
      const oldest = this.doubts.keys().next().value;
      if (oldest === undefined) break;
      this.doubts.delete(oldest);
    }
  }

  /** ¿Ese lote exacto de ese nodo está en duda? No toca el orden de desalojo. */
  isInDoubt(node: string, fingerprint: string): boolean {
    return this.doubts.get(node)?.has(fingerprint) ?? false;
  }

  /** Banca confirmó ESE lote o el operador eligió "reintentar como nuevo": deja de estar en duda. */
  forget(node: string, fingerprint: string): void {
    const set = this.doubts.get(node);
    if (!set) return;
    set.delete(fingerprint);
    if (set.size === 0) this.doubts.delete(node);
  }

  /** Cierre de sesión: no sobrevive ninguna marca. */
  clear(): void {
    this.doubts.clear();
  }

  /** Nodos con al menos un lote en duda. */
  get size(): number {
    return this.doubts.size;
  }
}
